(() => {
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const home = document.body.classList.contains('template-index');
  if (!home) return;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  let scrollQueued = false;

  const mountVideo = (section, kind) => {
    const mount = section.querySelector(`[data-${kind}-video-mount]`);
    if (!mount) return;
    const mode = mobileQuery.matches ? 'mobile' : 'desktop';
    if (mount.dataset.mode === mode) return;
    mount.replaceChildren();
    mount.dataset.mode = mode;
    const toggle = section.querySelector(`[data-${kind}-toggle]`);
    if (toggle) toggle.hidden = true;
    section.classList.remove('is-video-ready');
    if (motionQuery.matches) return;
    const template = section.querySelector(`[data-${kind}-video-${mode}]`);
    if (!template) return;
    mount.appendChild(template.content.cloneNode(true));
    const video = mount.querySelector('video');
    if (!video) return;
    video.removeAttribute('poster');
    video.querySelectorAll('img').forEach((image) => image.remove());
    if (toggle) {
      toggle.hidden = true;
      toggle.onclick = () => {
        if (video.paused) video.play().catch(() => {});
        else video.pause();
        toggle.textContent = video.paused ? 'Play film' : 'Pause film';
        toggle.setAttribute('aria-label', toggle.textContent);
      };
    }
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.addEventListener('playing', () => {
      section.classList.add('is-video-ready');
      if (toggle) {
        toggle.hidden = false;
        toggle.textContent = 'Pause film';
        toggle.setAttribute('aria-label', 'Pause film');
      }
    }, { once: true });
    const startVideo = () => video.play().catch(() => {});
    video.preload = 'auto';
    video.load();
    if (video.readyState >= 4) startVideo();
    else video.addEventListener('canplaythrough', startVideo, { once: true });
  };

  let hero = document.querySelector('[data-cinematic-hero]');
  if (hero) {
    mountVideo(hero, 'cinematic');
    mobileQuery.addEventListener('change', () => mountVideo(hero, 'cinematic'));
    motionQuery.addEventListener('change', () => mountVideo(hero, 'cinematic'));
  }

  const finalSections = new Set();
  const finalObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        mountVideo(entry.target, 'final');
        finalObserver.unobserve(entry.target);
      }
    });
  }, { rootMargin: '250px 0px' });
  const setupFinal = (root = document) => {
    root.querySelectorAll('[data-final-story]').forEach((section) => {
      if (finalSections.has(section)) return;
      finalSections.add(section);
      finalObserver.observe(section);
    });
  };
  setupFinal();
  mobileQuery.addEventListener('change', () => finalSections.forEach((section) => mountVideo(section, 'final')));
  motionQuery.addEventListener('change', () => finalSections.forEach((section) => mountVideo(section, 'final')));

  const revealObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.querySelectorAll('.sl-video-hero__card').forEach((card) => card.classList.add('is-revealed'));
      revealObserver.unobserve(entry.target);
    });
  }, { threshold: .14 });
  const setupUnboxing = (root = document) => {
    root.querySelectorAll('[data-unboxing]').forEach((section) => {
      section.querySelectorAll('.sl-video-hero__card').forEach((card) => card.setAttribute('data-reveal-pending', ''));
      if (motionQuery.matches) section.querySelectorAll('.sl-video-hero__card').forEach((card) => card.classList.add('is-revealed'));
      else revealObserver.observe(section);
    });
  };
  setupUnboxing();

  const productImage = document.querySelector('.template-index .featured-product__image');
  const costSections = new Set();
  const craftSections = new Set();
  const updateScroll = () => {
    scrollQueued = false;
    const viewport = window.innerHeight;
    document.body.classList.toggle('sl-header-scrolled', window.scrollY > 2);
    if (!motionQuery.matches && !mobileQuery.matches && productImage) {
      const rect = productImage.getBoundingClientRect();
      const travel = clamp((viewport / 2 - (rect.top + rect.height / 2)) / (viewport + rect.height), -.5, .5);
      productImage.style.setProperty('--product-parallax', `${(travel * 4).toFixed(2)}%`);
    } else if (productImage) productImage.style.removeProperty('--product-parallax');
    costSections.forEach((section) => {
      const arc = section.querySelector('[data-arc-stroke]');
      const number = section.querySelector('[data-night-count]');
      if (!arc || !number) return;
      const rect = section.getBoundingClientRect();
      const progress = motionQuery.matches ? 1 : clamp((viewport * .75 - rect.top) / (viewport * .75 + rect.height * .54), 0, 1);
      section.style.setProperty('--arc-offset', `${Math.round(591 * (1 - Math.max(.015, progress)))}`);
      number.textContent = progress >= .96 ? '365' : progress >= .68 ? '100' : progress >= .34 ? '30' : '1';
    });
    craftSections.forEach((section) => {
      const rect = section.getBoundingClientRect();
      const progress = motionQuery.matches ? 100 : clamp((viewport * .75 - rect.top) / (viewport * .7 + rect.height * .6), 0, 1) * 100;
      section.style.setProperty('--craft-progress', `${progress.toFixed(1)}%`);
    });
  };
  const queueScroll = () => {
    if (scrollQueued) return;
    scrollQueued = true;
    requestAnimationFrame(updateScroll);
  };
  window.addEventListener('scroll', queueScroll, { passive: true });
  window.addEventListener('resize', queueScroll, { passive: true });
  motionQuery.addEventListener('change', queueScroll);

  class CostOfInaction extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      costSections.add(this);
      this.skin = this.querySelector('[data-cost-input="skin"]');
      this.hair = this.querySelector('[data-cost-input="hair"]');
      this.onInput = () => this.updateCalculator();
      [this.skin, this.hair].filter(Boolean).forEach((input) => input.addEventListener('input', this.onInput));
      this.updateCalculator();
      queueScroll();
    }
    disconnectedCallback() {
      [this.skin, this.hair].filter(Boolean).forEach((input) => input.removeEventListener('input', this.onInput));
      costSections.delete(this);
      this.initialized = false;
    }
    updateCalculator() {
      if (!this.skin || !this.hair) return;
      const currency = this.dataset.currency || 'AUD';
      const money = new Intl.NumberFormat('en-AU', { style: 'currency', currency, maximumFractionDigits: 0 });
      const updateInput = (input, output) => {
        const amount = Number(input.value);
        output.textContent = money.format(amount);
        input.setAttribute('aria-valuetext', `${money.format(amount)} per month`);
        input.style.setProperty('--range-progress', `${100 * amount / Number(input.max)}%`);
        return amount;
      };
      const skin = updateInput(this.skin, this.querySelector('[data-skin-output]'));
      const hair = updateInput(this.hair, this.querySelector('[data-hair-output]'));
      this.querySelector('[data-annual-total]').textContent = money.format((skin + hair) * 12);
      const nightly = (skin + hair) * 12 / 365;
      const nightlyOutput = this.querySelector('[data-current-nightly]');
      if (nightlyOutput) nightlyOutput.textContent = String.fromCharCode(36) + nightly.toFixed(2);
      this.dispatchEvent(new CustomEvent('sl:cost-change', { bubbles: true, detail: { nightly } }));
    }
  }
  if (!customElements.get('cost-of-inaction')) customElements.define('cost-of-inaction', CostOfInaction);

  const setupCraft = (root = document) => {
    root.querySelectorAll('[data-craft-story]').forEach((section) => craftSections.add(section));
    queueScroll();
  };
  setupCraft();
  queueScroll();

  document.addEventListener('shopify:section:load', (event) => {
    setupFinal(event.target);
    setupUnboxing(event.target);
    setupCraft(event.target);
    const newHero = event.target.querySelector('[data-cinematic-hero]');
    if (newHero) { hero = newHero; mountVideo(newHero, 'cinematic'); }
    queueScroll();
  });
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-final-story]').forEach((section) => { finalObserver.unobserve(section); finalSections.delete(section); });
    event.target.querySelectorAll('[data-craft-story]').forEach((section) => craftSections.delete(section));
    event.target.querySelectorAll('[data-unboxing]').forEach((section) => revealObserver.unobserve(section));
  });
})();
